import React, { useMemo, useState } from "react";
import {
  MessageSquare,
  DollarSign,
  Users,
  Settings,
  Zap,
  ChevronDown,
  Calendar,
} from "lucide-react";

import { SimpleLineChart } from "../../../components/marketing/dashboard/line-chart";
import { TopResellersTable } from "../../../components/marketing/dashboard/top-sellers";
import { RecentActivity } from "../../../components/marketing/dashboard/recent-activity";
import { MetricCard } from "../../../components/marketing/dashboard/metrics-card";
import { DashboardData, FilterType, TimePeriod } from "../../../components/types";
import { Link } from "react-router-dom";
import { MessageTrendsChart } from "../../../components/marketing/dashboard/msg-trends";

export type ActivityItem = {
  type: "new" | "alert" | "revenue" | "system";
  message: string;
  time: string;
};

// Generate sample data for different periods or date ranges
const generateSampleData = (
  days: number,
  baseValue: number,
  promotional: number,
  startDate?: Date
) => {
  const data = [];
  const referenceDate = startDate || new Date();

  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(referenceDate);
    date.setDate(date.getDate() - i);
    const dateStr = date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });

    const variation = 0.8 + Math.random() * 0.4;
    const total = Math.round(baseValue * variation);
    const promo = Math.round(promotional * variation);
    const transactional = total - promo;

    data.push({
      date: dateStr,
      total,
      promotional: promo,
      transactional,
    });
  }

  return data;
};

const generateRevenueData = (
  days: number,
  baseRevenue: number,
  startDate?: Date
) => {
  const data = [];
  const referenceDate = startDate || new Date();

  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(referenceDate);
    date.setDate(date.getDate() - i);
    const dateStr = date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });

    const variation = 0.8 + Math.random() * 0.4;
    const revenue = Math.round(baseRevenue * variation);

    data.push({
      date: dateStr,
      revenue,
    });
  }

  return data;
};

// Function to calculate days between two dates
const getDaysBetween = (startDate: Date, endDate: Date) => {
  const timeDiff = endDate.getTime() - startDate.getTime();
  return Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1;
};

// Generate custom data for date range
const generateCustomRangeData = (
  startDate: Date,
  endDate: Date,
  filterType: FilterType
): DashboardData => {
  const days = getDaysBetween(startDate, endDate);

  // Base values based on filter type
  let baseMessage = 72000,
    basePromo = 43000,
    baseRevenue = 7200;
  let activeResellers = "156";
  let topResellers = [
    { name: "TechCorp Solutions", messages: "0", revenue: "₦0" },
    { name: "Digital Marketing Pro", messages: "0", revenue: "₦0" },
    { name: "SMS Masters", messages: "0", revenue: "₦0" },
    { name: "CloudText Inc", messages: "0", revenue: "₦0" },
    { name: "MessageFlow", messages: "0", revenue: "₦0" },
  ];

  // Create properly typed activity arrays
  const globalAdminActivities: ActivityItem[] = [
    {
      type: "new",
      message: 'New reseller "FastSMS Co" registered',
      time: "2 hours ago",
    },
    {
      type: "alert",
      message: "High volume detected from TechCorp Solutions",
      time: "4 hours ago",
    },
    {
      type: "revenue",
      message: "Custom period revenue target exceeded",
      time: "6 hours ago",
    },
    {
      type: "system",
      message: "System maintenance completed",
      time: "1 day ago",
    },
  ];

  const resellerActivities: ActivityItem[] = [
    {
      type: "revenue",
      message: "Custom period revenue target achieved",
      time: "1 hour ago",
    },
    {
      type: "new",
      message: 'New campaign "Summer Sale" launched',
      time: "3 hours ago",
    },
    {
      type: "alert",
      message: "High engagement rate detected",
      time: "5 hours ago",
    },
    {
      type: "system",
      message: "API rate limit increased",
      time: "8 hours ago",
    },
  ];

  const endCustomerActivities: ActivityItem[] = [
    {
      type: "new",
      message: "New SMS campaign created",
      time: "30 minutes ago",
    },
    {
      type: "revenue",
      message: "Custom period spending limit updated",
      time: "2 hours ago",
    },
    {
      type: "alert",
      message: "Low credit balance warning",
      time: "4 hours ago",
    },
    {
      type: "system",
      message: "Account verified successfully",
      time: "1 day ago",
    },
  ];

  let recentActivity: ActivityItem[] = globalAdminActivities;

  if (filterType === "Reseller") {
    baseMessage = 20800;
    basePromo = 13000;
    baseRevenue = 2080;
    activeResellers = "1";
    topResellers = [
      { name: "TechCorp Solutions", messages: "0", revenue: "0" },
    ];
    recentActivity = resellerActivities;
  } else if (filterType === "End Customer") {
    baseMessage = 1580;
    basePromo = 1180;
    baseRevenue = 158;
    activeResellers = "0";
    topResellers = [];
    recentActivity = endCustomerActivities;
  }

  // Calculate totals based on days
  const totalMessages = baseMessage * days;
  const totalRevenue = baseRevenue * days;
  const totalPromo = basePromo * days;

  // Format numbers
  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(2)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
    return num.toString();
  };

  const formatRevenue = (num: number) => {
    if (num >= 1000000) return `₦${(num / 1000000).toFixed(2)}M`;
    if (num >= 1000) return `₦${(num / 1000).toFixed(1)}K`;
    return `₦${num}`;
  };

  return {
    metrics: {
      totalMessages: {
        value: formatNumber(totalMessages),
        change: "+12.5%",
      },
      totalRevenue: {
        value: formatRevenue(totalRevenue),
        change: "+8.3%",
      },
      activeResellers: {
        value: activeResellers,
        change: "+5.2%",
      },
      promotionalMessages: {
        value: formatNumber(totalPromo),
        change: "59.7% of total",
      },
    },
    messageData: generateSampleData(days, baseMessage, basePromo, endDate),
    revenueData: generateRevenueData(days, baseRevenue, endDate),
    topResellers,
    recentActivity,
  };
};

// Create datasets for each combination of filter and period (keeping your original data)
const dashboardDataSets: Record<
  FilterType,
  Record<TimePeriod, DashboardData>
> = {
  "Global Admin": {
    "24h": {
      metrics: {
        totalMessages: { value: "0", change: "+0%" },
        totalRevenue: { value: "₦0", change: "+0%" },
        activeResellers: { value: "0", change: "+0%" },
        promotionalMessages: { value: "0", change: "0% of total" },
      },
      messageData: generateSampleData(0, 0, 0),
      revenueData: generateRevenueData(0, 0),
      topResellers: [
        { name: "TechCorp Solutions", messages: "0", revenue: "₦0" },
        { name: "Digital Marketing Pro", messages: "0", revenue: "₦0" },
        { name: "SMS Masters", messages: "0", revenue: "₦0" },
        { name: "CloudText Inc", messages: "0", revenue: "₦0" },
        { name: "MessageFlow", messages: "0", revenue: "₦0" },
      ],
      recentActivity: [
        {
          type: "new" as const,
          message: 'New reseller "FastSMS Co" registered',
          time: "2 hours ago",
        },
        {
          type: "alert" as const,
          message: "High volume detected from TechCorp Solutions",
          time: "4 hours ago",
        },
        {
          type: "revenue" as const,

          message: "Hourly revenue target exceeded",
          time: "6 hours ago",
        },
        {
          type: "system" as const,
          message: "System performance optimized",
          time: "8 hours ago",
        },
      ],
    },
    "7 days": {
      metrics: {
        totalMessages: { value: "0", change: "+0%" },
        totalRevenue: { value: "₦0", change: "+0%" },
        activeResellers: { value: "0", change: "+0%" },
        promotionalMessages: { value: "0", change: "0% of total" },
      },
      messageData: generateSampleData(0, 0, 0),
      revenueData: generateRevenueData(0, 0),
      topResellers: [
        { name: "TechCorp Solutions", messages: "0", revenue: "0" },
        { name: "Digital Marketing Pro", messages: "0", revenue: "0" },
        { name: "SMS Masters", messages: "0", revenue: "0" },
        { name: "CloudText Inc", messages: "0", revenue: "0" },
        { name: "MessageFlow", messages: "0", revenue: "0" },
      ],
      recentActivity: [
        {
          type: "new",
          message: 'New reseller "FastSMS Co" registered',
          time: "2 hours ago",
        },
        {
          type: "alert",
          message: "High volume detected from TechCorp Solutions",
          time: "4 hours ago",
        },
        {
          type: "revenue",
          message: "Weekly revenue target exceeded",
          time: "6 hours ago",
        },
        {
          type: "system",
          message: "System maintenance completed",
          time: "1 day ago",
        },
      ],
    },
    "30 days": {
      metrics: {
        totalMessages: { value: "0", change: "+0%" },
        totalRevenue: { value: "₦0", change: "0" },
        activeResellers: { value: "0", change: "0" },
        promotionalMessages: { value: "0", change: "0% of total" },
      },
      messageData: generateSampleData(0, 0, 0),
      revenueData: generateRevenueData(0, 0),
    topResellers: [
        { name: "TechCorp Solutions", messages: "0", revenue: "0" },
        { name: "Digital Marketing Pro", messages: "0", revenue: "0" },
        { name: "SMS Masters", messages: "0", revenue: "0" },
        { name: "CloudText Inc", messages: "0", revenue: "0" },
        { name: "MessageFlow", messages: "0", revenue: "0" },
      ],
      recentActivity: [
        {
          type: "new",
          message: 'New reseller "FastSMS Co" registered',
          time: "2 hours ago",
        },
        {
          type: "alert",
          message: "High volume detected from TechCorp Solutions",
          time: "4 hours ago",
        },
        {
          type: "revenue",
          message: "Monthly revenue target exceeded",
          time: "6 hours ago",
        },
        {
          type: "system",
          message: "System maintenance completed",
          time: "1 day ago",
        },
      ],
    },
    "90 days": {
      metrics: {
        totalMessages: { value: "0", change: "+0%" },
        totalRevenue: { value: "₦0", change: "+0" },
        activeResellers: { value: "0", change: "+0%" },
        promotionalMessages: { value: "0", change: "0% of total" },
      },
      messageData: generateSampleData(0, 0, 0),
      revenueData: generateRevenueData(0, 0),
      topResellers: [
        { name: "TechCorp Solutions", messages: "0", revenue: "₦0" },
        { name: "Digital Marketing Pro", messages: "0", revenue: "₦0" },
        { name: "SMS Masters", messages: "0", revenue: "₦0" },
        { name: "CloudText Inc", messages: "0", revenue: "₦0" },
        { name: "MessageFlow", messages: "0", revenue: "₦0" },
      ],
      recentActivity: [
        {
          type: "new",
          message: 'New reseller "FastSMS Co" registered',
          time: "2 hours ago",
        },
        {
          type: "alert",
          message: "High volume detected from TechCorp Solutions",
          time: "4 hours ago",
        },
        {
          type: "revenue",
          message: "Quarterly revenue target exceeded",
          time: "6 hours ago",
        },
        {
          type: "system",
          message: "System maintenance completed",
          time: "1 day ago",
        },
      ] as ActivityItem[],
    },
  },
  Reseller: {
    "24h": {
      metrics: {
        totalMessages: { value: "20.8K", change: "+15.4%" },
        totalRevenue: { value: "₦2.08K", change: "+12.1%" },
        activeResellers: { value: "1", change: "0%" },
        promotionalMessages: { value: "13K", change: "62.5% of total" },
      },
      messageData: generateSampleData(0, 0, 0),
      revenueData: generateRevenueData(0, 0),
      topResellers: [
        { name: "TechCorp Solutions", messages: "20.8K", revenue: "₦2.08K" },
      ],
      recentActivity: [
        {
          type: "revenue",
          message: "Daily revenue target achieved",
          time: "1 hour ago",
        },
        {
          type: "new",
          message: 'Campaign "Flash Sale" launched',
          time: "3 hours ago",
        },
        {
          type: "alert",
          message: "High engagement rate detected",
          time: "5 hours ago",
        },
        {
          type: "system",
          message: "API rate limit increased",
          time: "8 hours ago",
        },
      ] as ActivityItem[],
    },
    "7 days": {
      metrics: {
        totalMessages: { value: "145.6K", change: "+8.2%" },
        totalRevenue: { value: "₦14.56K", change: "+6.1%" },
        activeResellers: { value: "1", change: "0%" },
        promotionalMessages: { value: "91K", change: "62.5% of total" },
      },
      messageData: generateSampleData(0, 0, 0),
      revenueData: generateRevenueData(0, 0),
      topResellers: [
        { name: "TechCorp Solutions", messages: "145.6K", revenue: "₦14.56K" },
      ],
      recentActivity: [
        {
          type: "revenue",
          message: "Weekly revenue target achieved",
          time: "1 hour ago",
        },
        {
          type: "new",
          message: 'New campaign "Summer Sale" launched',
          time: "3 hours ago",
        },
        {
          type: "alert",
          message: "High engagement rate detected",
          time: "5 hours ago",
        },
        {
          type: "system",
          message: "API rate limit increased",
          time: "8 hours ago",
        },
      ],
    },
    "30 days": {
      metrics: {
        totalMessages: { value: "624K", change: "+7.1%" },
        totalRevenue: { value: "₦62.4K", change: "+5.8%" },
        activeResellers: { value: "1", change: "0%" },
        promotionalMessages: { value: "390K", change: "62.5% of total" },
      },
      messageData: generateSampleData(0, 0, 0),
      revenueData: generateRevenueData(0, 0),
      topResellers: [
        { name: "TechCorp Solutions", messages: "624K", revenue: "₦62.4K" },
      ],
      recentActivity: [
        {
          type: "revenue",
          message: "Monthly revenue target achieved",
          time: "1 hour ago",
        },
        {
          type: "new",
          message: 'New campaign "Summer Sale" launched',
          time: "3 hours ago",
        },
        {
          type: "alert",
          message: "High engagement rate detected",
          time: "5 hours ago",
        },
        {
          type: "system",
          message: "API rate limit increased",
          time: "8 hours ago",
        },
      ] as ActivityItem[],
    },
    "90 days": {
      metrics: {
        totalMessages: { value: "1.87M", change: "+6.3%" },
        totalRevenue: { value: "₦187.2K", change: "+5.1%" },
        activeResellers: { value: "1", change: "0%" },
        promotionalMessages: { value: "1.17M", change: "62.5% of total" },
      },
      messageData: generateSampleData(0, 0, 0),
      revenueData: generateRevenueData(0, 0),
      topResellers: [
        { name: "TechCorp Solutions", messages: "1.87M", revenue: "₦187.2K" },
      ],
      recentActivity: [
        {
          type: "revenue",
          message: "Quarterly revenue target achieved",
          time: "1 hour ago",
        },
        {
          type: "new",
          message: 'New campaign "Summer Sale" launched',
          time: "3 hours ago",
        },
        {
          type: "alert",
          message: "High engagement rate detected",
          time: "5 hours ago",
        },
        {
          type: "system",
          message: "API rate limit increased",
          time: "8 hours ago",
        },
      ] as ActivityItem[],
    },
  },
  "End Customer": {
    "24h": {
      metrics: {
        totalMessages: { value: "1.58K", change: "+22.1%" },
        totalRevenue: { value: "₦158", change: "+19.8%" },
        activeResellers: { value: "0", change: "0%" },
        promotionalMessages: { value: "1.18K", change: "74.7% of total" },
      },
      messageData: generateSampleData(0, 0, 0),
      revenueData: generateRevenueData(1, 158),
      topResellers: [],
      recentActivity: [
        {
          type: "new",
          message: "New SMS campaign created",
          time: "30 minutes ago",
        },
        {
          type: "revenue",
          message: "Daily spending limit updated",
          time: "2 hours ago",
        },
        {
          type: "alert",
          message: "High delivery rate achieved",
          time: "4 hours ago",
        },
        {
          type: "system",
          message: "Account verified successfully",
          time: "6 hours ago",
        },
      ],
    },
    "7 days": {
      metrics: {
        totalMessages: { value: "11.06K", change: "+15.3%" },
        totalRevenue: { value: "₦1.11K", change: "+12.7%" },
        activeResellers: { value: "0", change: "0%" },
        promotionalMessages: { value: "8.26K", change: "74.7% of total" },
      },
      messageData: generateSampleData(0, 0, 0),
      revenueData: generateRevenueData(0, 0),
      topResellers: [],
      recentActivity: [
        {
          type: "new",
          message: "New SMS campaign created",
          time: "30 minutes ago",
        },
        {
          type: "revenue",
          message: "Weekly spending limit updated",
          time: "2 hours ago",
        },
        {
          type: "alert",
          message: "Low credit balance warning",
          time: "4 hours ago",
        },
        {
          type: "system",
          message: "Account verified successfully",
          time: "1 day ago",
        },
      ] as ActivityItem[],
    },
    "30 days": {
      metrics: {
        totalMessages: { value: "47.4K", change: "+13.8%" },
        totalRevenue: { value: "₦4.74K", change: "+11.2%" },
        activeResellers: { value: "0", change: "0%" },
        promotionalMessages: { value: "35.4K", change: "74.7% of total" },
      },
      messageData: generateSampleData(0, 0, 0),
      revenueData: generateRevenueData(0, 0),
      topResellers: [],
      recentActivity: [
        {
          type: "new",
          message: "New SMS campaign created",
          time: "30 minutes ago",
        },
        {
          type: "revenue",
          message: "Monthly spending limit updated",
          time: "2 hours ago",
        },
        {
          type: "alert",
          message: "Low credit balance warning",
          time: "4 hours ago",
        },
        {
          type: "system",
          message: "Account verified successfully",
          time: "1 day ago",
        },
      ] as ActivityItem[],
    },
    "90 days": {
      metrics: {
        totalMessages: { value: "142.2K", change: "+11.9%" },
        totalRevenue: { value: "₦14.22K", change: "+9.8%" },
        activeResellers: { value: "0", change: "0%" },
        promotionalMessages: { value: "106.2K", change: "74.7% of total" },
      },
      messageData: generateSampleData(0, 0, 0),
      revenueData: generateRevenueData(0, 0),
      topResellers: [],
      recentActivity: [
        {
          type: "new",
          message: "New SMS campaign created",
          time: "30 minutes ago",
        },
        {
          type: "revenue",
          message: "Quarterly spending limit updated",
          time: "2 hours ago",
        },
        {
          type: "alert",
          message: "Low credit balance warning",
          time: "4 hours ago",
        },
        {
          type: "system",
          message: "Account verified successfully",
          time: "1 day ago",
        },
      ] as ActivityItem[],
    },
  },
};

const SMSDashboard: React.FC = () => {
  const [selectedFilter, setSelectedFilter] =
    useState<FilterType>("Global Admin");
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>("7 days");
  const [open, setOpen] = useState(false);
  const [dateRangeOpen, setDateRangeOpen] = useState(false);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [customDateRange, setCustomDateRange] = useState<{
    start: Date;
    end: Date;
  } | null>(null);

  // Initialize dates
  React.useEffect(() => {
    const today = new Date();
    const lastWeek = new Date(today);
    lastWeek.setDate(lastWeek.getDate() - 7);

    setStartDate(lastWeek.toISOString()?.split("T")[0]);
    setEndDate(today.toISOString()?.split("T")[0]);
  }, []);

  const currentData = useMemo(() => {
    // If custom date range is selected, generate custom data
    if (customDateRange) {
      return generateCustomRangeData(
        customDateRange.start,
        customDateRange.end,
        selectedFilter
      );
    }

    // Otherwise use the existing predefined data
    return dashboardDataSets[selectedFilter][selectedPeriod];
  }, [selectedFilter, selectedPeriod, customDateRange]);

  const handleFilterChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedFilter(event.target.value as FilterType);
  };

  const handlePeriodChange = (period: TimePeriod) => {
    setSelectedPeriod(period);
    // Clear custom date range when selecting predefined periods
    setCustomDateRange(null);
    setDateRangeOpen(false);
  };

  const handleDateRangeClick = () => {
    setDateRangeOpen(true);
    setSelectedPeriod("7 days"); // Reset to default or keep current
  };

  const handleDateRangeApply = () => {
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);

      if (start <= end) {
        setCustomDateRange({ start, end });
        setDateRangeOpen(false);
      } else {
        alert("Start date must be before or equal to end date");
      }
    }
  };

  const handleDateRangeCancel = () => {
    setDateRangeOpen(false);
    setCustomDateRange(null);
  };

  const formatDateRangeDisplay = (): string => {
    if (customDateRange) {
      const start = customDateRange.start.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
      const end = customDateRange.end.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
      return `${start} - ${end}`;
    }
    return "Select custom date range"; // Return default string instead of null
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-800">
      {/* Header */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-white" />
              </div>
              <div className="relative inline-block text-left">
                <button
                  onClick={() => setOpen(!open)}
                  className="inline-flex items-center gap-1 text-sm font-semibold text-gray-900 dark:text-gray-100 hover:text-blue-600"
                >
                  ConvoHub Platform Dashboard
                  <ChevronDown size={16} />
                </button>

                {open && (
                  <div className="absolute left-0 mt-2 w-56 rounded-md shadow-lg bg-white dark:bg-gray-900 ring-1 ring-black ring-opacity-5 z-50">
                    <div className="py-1">
                      <Link
                        to="/cpas-dashboard"
                        className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                        onClick={() => setOpen(false)}
                      >
                        Multi Channel Dashboard
                      </Link>
                      {/* Add more links here if needed */}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-1 relative">
              {(["24h", "7 days", "30 days", "90 days"] as TimePeriod[]).map(
                (period) => (
                  <button
                    key={period}
                    onClick={() => handlePeriodChange(period)}
                    className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                      selectedPeriod === period && !customDateRange
                        ? "bg-blue-600 text-white"
                        : "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
                    }`}
                  >
                    {period}
                  </button>
                )
              )}

              {/* Custom Date Range Button */}
              <div className="relative">
                <button
                  onClick={handleDateRangeClick}
                  className={`px-3 py-1 text-sm font-medium rounded-md transition-colors flex items-center gap-1 ${
                    customDateRange
                      ? "bg-blue-600 text-white"
                      : "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
                  }`}
                  // title={
                  //   customDateRange
                  //     ? formatDateRangeDisplay()
                  //     : "Select custom date range"
                  // }
                  title={formatDateRangeDisplay()}
                >
                  <Calendar size={14} />
                  {customDateRange ? formatDateRangeDisplay() : "Custom"}
                </button>

                {/* Date Range Picker Dropdown */}
                {dateRangeOpen && (
                  <div className="absolute right-0 mt-2 w-80 rounded-md shadow-lg bg-white dark:bg-gray-900 ring-1 ring-black ring-opacity-5 z-50 p-4">
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Start Date
                        </label>
                        <input
                          type="date"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          End Date
                        </label>
                        <input
                          type="date"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm"
                        />
                      </div>
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={handleDateRangeCancel}
                          className="px-3 py-1 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleDateRangeApply}
                          className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Don't remove  */}
            {/* <select
              className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm"
              value={selectedFilter}
              onChange={handleFilterChange}
            >
              <option value="Global Admin">Global Admin</option>
              <option value="Reseller">Reseller</option>
              <option value="End Customer">End Customer</option>
            </select> */}
            <button className="p-2 text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
              <Link to="/settings">
                <Settings />
              </Link>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6">
        {/* Metrics Cards */}
        <div className="grid grid-cols-1 gap-6 mb-6">
          <MetricCard
            title="Total Messages"
            value={currentData.metrics.totalMessages.value}
            change={currentData.metrics.totalMessages.change}
            icon={<MessageSquare className="w-6 h-6 text-blue-600 dark:text-blue-400" />}
            trend="up"
          />
          <MetricCard
            title="Total Revenue"
            value={currentData.metrics.totalRevenue.value}
            change={currentData.metrics.totalRevenue.change}
            icon={<DollarSign className="w-6 h-6 text-green-600 dark:text-green-400" />}
            trend="up"
          />
          <MetricCard
            title="Active Resellers"
            value={currentData.metrics.activeResellers.value}
            change={currentData.metrics.activeResellers.change}
            icon={<Users className="w-6 h-6 text-purple-600 dark:text-purple-400" />}
            trend="up"
          />
          <MetricCard
            title="Promotional Messages"
            value={currentData.metrics.promotionalMessages.value}
            change={currentData.metrics.promotionalMessages.change}
            icon={<Zap className="w-6 h-6 text-orange-600 dark:text-orange-400" />}
            trend="up"
          />
        </div>

        <div>
          <MessageTrendsChart data={currentData.messageData} />
        </div>

        {/* Revenue Trend and Top Resellers */}
        <div className="grid grid-cols-1 gap-6 mb-6 my-6">
          <div className="bg-white dark:bg-gray-900 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Revenue Trend
            </h3>
            <SimpleLineChart data={currentData.revenueData} type="revenue" />
          </div>
          <TopResellersTable data={currentData.topResellers} type="reseller" />
        </div>

        {/* Recent Activity */}
        <RecentActivity data={currentData.recentActivity} type="activity" />
      </div>
    </div>
  );
};

export default SMSDashboard;
